import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Grid2,
  Avatar,
  Stack,
  IconButton,
  useTheme,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Security,
  CheckCircle,
  NoMeals,
  CalendarMonth,
  LocationOn,
} from "@mui/icons-material";
import {
  Place as PlaceIcon,
  Train as TrainIcon,
  LocalAirport as LocalAirportIcon,
  // FlightTakeoff as FlightIcon,
  DirectionsBus as DirectionsBusIcon,
  LocalTaxi as LocalTaxiIcon,
} from "@mui/icons-material";
import PriceDisplay from "../PriceDisplay";

// Icon Mapper Helper
const getTransportIcon = (iconName) => {
  switch (iconName) {
    case "SubwayIcon":
      return <TrainIcon />;
    case "BusIcon":
      return <DirectionsBusIcon />;
    case "TrainIcon":
      return <TrainIcon />;
    case "FlightIcon":
      return <LocalAirportIcon />;
    case "TaxiIcon":
      return <LocalTaxiIcon />;
    default:
      return <PlaceIcon />;
  }
};

// Reusable Helper for the Dialog Rows
const InfoRow = ({ icon, label, value }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar sx={{ width: 32, height: 32 }}>{icon}</Avatar>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
    <Box>
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography variant="subtitle2" fontWeight="bold">
          {value}
        </Typography>
      )}
    </Box>
  </Box>
);

const SectionHeader = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    fontWeight="bold"
    display="block"
    mt={2}
    mb={1}
  >
    {children}
  </Typography>
);

const UnitDetailsDialog = ({ open, onClose, unit, property }) => {
  const theme = useTheme();
  if (!unit) return null;

  const food = unit.foodDetails || {};
  const transport = property.nearbyTransport || {};

  const toTitleCase = (s = "") =>
    s
      .split(/[\s-_]+/) // split on spaces, hyphens, underscores
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
      .join(" ");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* <Typography variant="h6">
          Room {unit.prefix}-{unit.number} Details
        </Typography> */}
        <Box>
          <Typography variant="h6">
            Room {unit.prefix}-{unit.number} Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unit.unitType?.name} • {unit.size} sqft
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* <DialogContent dividers> */}
      <DialogContent sx={{ p: 0 }}>
        {unit.comments && (
          <Box p={3} pb={1}>
            <Typography variant="body2" color="text.secondary">
              {unit.comments}
            </Typography>
          </Box>
        )}

        {/* <Stack spacing={3}> */}
        <Stack spacing={0} px={3} pb={3}>
          {/* Section 1: Financials */}
          {/* --- SECTION 1: FINANCIALS --- */}
          <SectionHeader>Financials & Terms</SectionHeader>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <InfoRow
              icon={<Security />}
              label="Security Deposit"
              value={
                <PriceDisplay
                  sx={{ fontWeight: "bold" }}
                  iconSize={16}
                  unitPrice={unit.securityDeposit}
                />
              }
            />
            <Divider sx={{ my: 1, borderStyle: "dashed" }} />
            <InfoRow
              icon={<CalendarMonth />}
              label="Notice Period"
              value={`${unit.noticePeriodDays || 30} Days`}
            />
            {/* If you have electricity info, add it here */}
            {/* <InfoRow icon={<FlashOn />} label="Electricity" value="Metered" /> */}
          </Paper>

          {/* --- SECTION 2: LOCATION --- */}
          {(property.nearbyTransport?.length > 0 ||
            property.nearestLandmark) && (
            <>
              <SectionHeader>Location & Commute</SectionHeader>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                {property.nearbyTransport?.map((item, index) => (
                  <React.Fragment key={index}>
                    <InfoRow
                      // icon={<LocationOn />}
                      icon={getTransportIcon(item.icon)}
                      label={item.name || "Transit"}
                      value={`${item.stationName} (${
                        item.distanceMetres >= 1000
                          ? `${(item.distanceMetres / 1000).toFixed(1)} km`
                          : `${item.distanceMetres} m`
                      })`}
                    />
                    {index < property.nearbyTransport.length - 1 && (
                      <Divider sx={{ my: 1, borderStyle: "dashed" }} />
                    )}
                  </React.Fragment>
                ))}

                {property.nearestLandmark && (
                  <>
                    <Divider sx={{ my: 1, borderStyle: "dashed" }} />
                    <InfoRow
                      icon={<LocationOn />}
                      label="Nearest Landmark"
                      value={`${property.nearestLandmark} (${
                        property.landmarkDistance >= 1000
                          ? `${(property.landmarkDistance / 1000).toFixed(
                              1
                            )} km`
                          : `${property.landmarkDistance} m`
                      })`}
                    />
                  </>
                )}
              </Paper>
            </>
          )}

          {/* --- SECTION 3: FOOD --- */}
          <SectionHeader>Food & Dining</SectionHeader>
          {food.isAvailable ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "primary.50",
                borderColor: "primary.200",
              }}
            >
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Preference
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    sx={{ mt: 0.5 }}
                  >
                    {food.dietaryPreference === "both"
                      ? "Veg & Non-Veg"
                      : toTitleCase(food.dietaryPreference)}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Meals
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0.5 }}
                  >
                    {food.mealsProvided?.map((meal) => (
                      <Chip
                        key={meal}
                        label={toTitleCase(meal)}
                        size="small"
                        color="primary"
                        variant="outlined" // Filled pops more for food
                        // sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    ))}
                  </Stack>
                </Grid2>
              </Grid2>
            </Paper>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              p={1}
              color="text.disabled"
            >
              <NoMeals fontSize="small" />
              <Typography variant="body2">No food service included.</Typography>
            </Box>
          )}

          {/* --- SECTION 4: AMENITIES --- */}
          <SectionHeader>Room Amenities</SectionHeader>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {unit.amenities?.length > 0 ? (
              unit.amenities.map((amenity) => (
                <Chip
                  key={amenity._id}
                  label={amenity.name}
                  icon={<CheckCircle fontSize="small" />}
                  size="small"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.disabled">
                No specific room amenities listed.
              </Typography>
            )}
          </Box>

          {/* <Box> */}
          {/* <Typography
              variant="overline"
              color="text.secondary"
              fontWeight="bold"
            >
              Financials & Terms
            </Typography>
            <InfoRow
              icon={<Security fontSize="small" />}
              label="Security Deposit"
              value={
                <PriceDisplay
                  sx={{ fontWeight: "bold" }}
                  iconSize={16}
                  unitPrice={unit.securityDeposit}
                />
              }
            /> */}
          {/* <InfoRow
              icon={<DirectionsBus fontSize="small" />}
              label="Nearest Transit"
              value={`${transport.stationName} (${
                transport.distanceMetres || 0
              } m)`}
            /> */}
          {/* {
              property.nearbyTransport?.length > 0 &&
                property.nearbyTransport.map((item, index) => (
                  <InfoRow
                    key={item.transportTypeId || index}
                    icon={<DirectionsBus fontSize="small" />}
                    label={item.name || "Nearest Transit"} // Use snapshotted name (Metro, Bus, etc)
                    value={`${item.stationName} (${
                      item.distanceMetres >= 1000
                        ? `${(item.distanceMetres / 1000).toFixed(1)} km`
                        : `${item.distanceMetres} m`
                    })`}
                  />
                ))
              // : (
              //   <InfoRow
              //     icon={<DirectionsBus fontSize="small" />}
              //     label="Transit"
              //     value="None nearby"
              //   />
              // )
            } */}
          {/* {property.nearestLandmark && (
              <InfoRow
                icon={<DirectionsBus fontSize="small" />}
                label="Nearest Landmark"
                value={`${property.nearestLandmark} (${
                  // property.landmarkDistance || 0
                  property.landmarkDistance >= 1000
                    ? `${(property.landmarkDistance / 1000).toFixed(1)} km`
                    : `${property.landmarkDistance} m`
                })`}
              />
            )} */}
          {/* </Box> */}

          {/* <Divider /> */}

          {/* Section 2: Food & Dining */}
          {/* <Box> */}
          {/* <Typography
              variant="overline"
              color="text.secondary"
              fontWeight="bold"
            >
              Food & Dining
            </Typography>
            {food.isAvailable ? (
              <Box
                // bgcolor="primary.light"
                // bgcolor="action.hover"
                // bgopacity={0.1}
                // p={2}
                // borderRadius={2}
                // mt={1}

                sx={{
                  bgcolor: food.isAvailable ? "primary.soft" : "action.hover",
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              > */}
          {/* <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Preference</Typography>
                    <Typography variant="subtitle2">
                      {food.dietaryPreference}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" display="block" mb={1}>
                      Meals Provided:
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {food.mealsProvided?.map((m) => (
                        <Chip
                          key={m}
                          label={m}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Dietary Preference
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {unit.foodDetails.dietaryPreference === "both"
                        ? "Veg & Non Veg"
                        : toTitleCase(unit.foodDetails.dietaryPreference)}
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Meals Included
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {unit.foodDetails.mealsProvided.map((meal) => (
                        <Chip
                          key={meal}
                          label={toTitleCase(meal)}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Stack>
                  </Grid2>
                </Grid2>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mt={1}
                color="text.disabled"
              >
                <NoMeals />{" "}
                <Typography variant="body2">
                  No food service included.
                </Typography>
              </Box>
            )}
          </Box> */}

          {/* <Divider /> */}

          {/* Section 3: Amenities */}
          {/* <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight="bold"
            >
              Amenities
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {unit.amenities?.map((amenity) => (
                <Chip
                  key={amenity._id}
                  label={amenity.name}
                  icon={<CheckCircle fontSize="small" />}
                  size="small"
                />
              ))}
              {(!unit.amenities || unit.amenities.length === 0) && (
                <Typography variant="caption" color="text.disabled">
                  None listed
                </Typography>
              )}
            </Box>
          </Box> */}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}
      >
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close
        </Button>
        {/* Add specific Call to Actions here if needed, like 'Book Now' */}
      </DialogActions>
    </Dialog>
  );
};

export default UnitDetailsDialog;
